'use strict';

/**
 * PictView-Ask: the user-facing Ask area (V50 User Tasks). A person browses the tenant's Active tasks, picks
 * one, fills the task's prompt shape, and files a Request; the view shows live progress and the answer, and
 * keeps a history of what they have asked. This is the friendly front door onto the generic node dispatch, kept
 * deliberately separate from the ops-flavored Fleet > Runs page.
 *
 * It reuses the generic EntityAPI providers for reads (TaskAPI for the Active catalog, PromptShapeAPI for a
 * task's input shape, RequestAPI for a person's own requests -- server-scoped to the asker) and AskAPI for the
 * one custom write (POST /1.0/Request/Submit). The body is rendered imperatively into #Ask-Body so the dynamic
 * form and the poll can repaint just that region without re-rendering the shell.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libMarkdownRender = require('./MarkdownRender.js');

// A request is done when it reaches one of these; the poll stops and the answer (or failure) is shown.
const _REQUEST_TERMINAL = { Answered: true, Failed: true, Cancelled: true };
const _POLL_MS = 4000;

// Inline trash glyph for the delete control. This view paints innerHTML directly, so the Pict {~I:~} icon
// macro is not expanded here; the SVG inherits the button's color via currentColor.
const _TRASH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>';

class PictViewAsk extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._tasks = [];
		this._myRequests = [];
		this._shapeSegments = [];
		this._selectedHasShape = false;
		this._selectedTask = null;
		this._activeIDRequest = 0;
		// Files the person has attached but not yet sent (uploaded to the Media buffer, referenced by IDMedia).
		this._pendingAttachments = [];
		// Whether the open conversation's task permits attachments (gates the chat composer's attach control).
		this._chatAllowAttachments = false;
		// The last-painted thread signature, so the poll repaints only on a real change.
		this._lastThreadSig = '';
		// A monotonically rising token so a poll scheduled before a navigation cannot paint over a later screen.
		this._pollToken = 0;
	}

	// True while any attached file is still uploading; sending now would silently drop it.
	_hasPendingUploads() { return this._pendingAttachments.some((pAtt) => pAtt && pAtt.Pending); }

	_taskAPI() { return this.pict.providers.TaskAPI; }
	_shapeAPI() { return this.pict.providers.PromptShapeAPI; }
	_requestAPI() { return this.pict.providers.RequestAPI; }
	_messageAPI() { return this.pict.providers.RequestMessageAPI; }
	_askAPI() { return this.pict.providers.AskAPI; }
	_mediaAPI() { return this.pict.providers.MediaAPI; }

	_esc(pValue)
	{
		return String(pValue === null || typeof pValue === 'undefined' ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	_body() { return (typeof document !== 'undefined') ? document.getElementById('Ask-Body') : null; }
	_paint(pHTML) { let tmpBody = this._body(); if (tmpBody) { tmpBody.innerHTML = pHTML; } }
	_app() { return this.pict.PictApplication; }

	// The shell holds #Ask-Body; render it only when it is not already on screen. A within-Ask navigation
	// (catalog -> task -> request) just repaints the body; a navigation in from another page renders the
	// shell first.
	_ensureShell() { if (!this._body()) { this.render(); } }

	// Each Ask state is its own ROUTE (/ask, /ask/task/:id, /ask/request/:id), so Back, forward and reload
	// all work and every state deep-links. The app's route handlers call showCatalog / showTask / showRequest;
	// the user actions below (pickTask, openRequest, backToCatalog, submit) NAVIGATE rather than repaint, which
	// is what keeps the history honest. Repainting #Ask-Body in place without changing the URL was the
	// regression that broke the Back button.
	show() { return this.showCatalog(); }

	// ---- catalog (route /ask) ------------------------------------------------------------------------------
	showCatalog()
	{
		this._ensureShell();
		this._enterCatalog();
		return true;
	}

	async _enterCatalog()
	{
		this._pollToken++;
		this._selectedTask = null;
		this._activeIDRequest = 0;
		this._paint('<div class="ask-loading">Loading tasks...</div>');
		let tmpTasks = [];
		let tmpRequests = [];
		try
		{
			// Active tasks only, tenant-scoped by the server; requests are server-scoped to this asker.
			tmpTasks = await this._taskAPI().resolve({ Filter: 'FBV~Status~EQ~Active', PageSize: 250 });
			tmpRequests = await this._requestAPI().resolve({ PageSize: 50 });
		}
		catch (pError) { /* a transient failure paints an empty catalog rather than a blank pane */ }
		this._tasks = Array.isArray(tmpTasks) ? tmpTasks : [];
		this._myRequests = (Array.isArray(tmpRequests) ? tmpRequests : [])
			.sort((pA, pB) => (Number(pB.RequestNumber) || 0) - (Number(pA.RequestNumber) || 0));
		this._paintCatalog();
	}

	_paintCatalog()
	{
		let tmpParts = [];
		if (!this._tasks.length)
		{
			tmpParts.push('<div class="ask-empty">No tasks yet. An administrator sets these up under Tasks.</div>');
		}
		else
		{
			// Group by Audience so a person sees the tasks meant for their role together.
			let tmpGroups = {};
			let tmpOrder = [];
			this._tasks.forEach((pTask) =>
			{
				let tmpAudience = String(pTask.Audience || '').trim() || 'General';
				if (!tmpGroups[tmpAudience]) { tmpGroups[tmpAudience] = []; tmpOrder.push(tmpAudience); }
				tmpGroups[tmpAudience].push(pTask);
			});
			tmpOrder.sort();
			tmpOrder.forEach((pAudience) =>
			{
				tmpParts.push('<div class="ask-group"><div class="ask-group-head">' + this._esc(pAudience) + '</div><div class="ask-cards">');
				tmpGroups[pAudience].forEach((pTask) =>
				{
					tmpParts.push(''
						+ '<button class="ask-card" onclick="_Pict.views[\'Plansheet-Ask\'].pickTask(' + (Number(pTask.IDTask) || 0) + ')">'
						+ '<div class="ask-card-name">' + this._esc(pTask.Name || ('Task ' + (pTask.TaskNumber || ''))) + '</div>'
						+ (pTask.Summary ? ('<div class="ask-card-sum">' + this._esc(pTask.Summary) + '</div>') : '')
						+ '</button>');
				});
				tmpParts.push('</div></div>');
			});
		}
		tmpParts.push(this._historyHTML());
		this._paint(tmpParts.join(''));
	}

	_historyHTML()
	{
		if (!this._myRequests.length) { return ''; }
		let tmpRows = this._myRequests.slice(0, 20).map((pRequest) =>
		{
			let tmpStatus = String(pRequest.Status || 'Pending');
			let tmpID = Number(pRequest.IDRequest) || 0;
			// The row is a flex container of two siblings (open, delete) rather than a single button, so the
			// delete control is not a button nested inside a button (invalid, and it would swallow the open click).
			return ''
				+ '<div class="ask-hist-row">'
				+ '<button class="ask-hist-open" onclick="_Pict.views[\'Plansheet-Ask\'].openRequest(' + tmpID + ')">'
				+ '<span class="ask-hist-name">' + this._esc(pRequest.Name || ('Request ' + (pRequest.RequestNumber || ''))) + '</span>'
				+ '<span class="ask-pill ask-pill-' + tmpStatus.toLowerCase() + '">' + this._esc(tmpStatus) + '</span>'
				+ '</button>'
				+ '<button class="ask-hist-del" title="Delete this request" aria-label="Delete this request" onclick="_Pict.views[\'Plansheet-Ask\'].deleteRequest(' + tmpID + ', false)">' + _TRASH_SVG + '</button>'
				+ '</div>';
		}).join('');
		return '<div class="ask-hist"><div class="ask-group-head">Your recent requests</div>' + tmpRows + '</div>';
	}

	// ---- the input form (route /ask/task/:id) --------------------------------------------------------------
	// Clicking a task card is a navigation to /ask/task/:id; the route handler calls showTask to load + paint.
	pickTask(pIDTask) { this._goTask(pIDTask); }

	async showTask(pIDTask)
	{
		this._ensureShell();
		this._pollToken++;
		this._activeIDRequest = 0;
		let tmpID = Number(pIDTask) || 0;
		this._paint('<div class="ask-loading">Loading...</div>');
		let tmpTask = this._tasks.find((pTask) => Number(pTask.IDTask) === tmpID);
		// Cold deep-link or reload: the catalog is not in memory, so fetch the one task by id. The read is
		// tenant-scoped server-side, so an unknown or cross-tenant id reads null and falls back to the catalog.
		if (!tmpTask && tmpID) { try { tmpTask = await this._taskAPI().read(tmpID); } catch (pError) { tmpTask = null; } }
		if (!tmpTask) { return this.showCatalog(); }
		this._selectedTask = tmpTask;
		this._pendingAttachments = [];
		this._shapeSegments = [];
		this._selectedHasShape = Number(tmpTask.IDPromptShape) > 0;
		if (Number(tmpTask.IDPromptShape) > 0)
		{
			try
			{
				let tmpShape = await this._shapeAPI().read(Number(tmpTask.IDPromptShape));
				let tmpDefinition = {};
				try { tmpDefinition = JSON.parse((tmpShape && tmpShape.ShapeDefinition) || '{}') || {}; } catch (pError) { tmpDefinition = {}; }
				// Only the editable (non-Fixed) segments are the user's to fill; Fixed segments are the admin's
				// locked skeleton, applied server-side from the shape, never sent as input.
				this._shapeSegments = (Array.isArray(tmpDefinition.Segments) ? tmpDefinition.Segments : [])
					.filter((pSegment) => pSegment && !pSegment.Fixed)
					.map((pSegment) => ({ Key: String(pSegment.Key || ''), Name: String(pSegment.Name || pSegment.Key || ''), Guidance: String(pSegment.Guidance || ''), Optional: !!pSegment.Optional }));
			}
			catch (pError) { this._shapeSegments = []; }
		}
		this._paintForm();
	}

	_paintForm()
	{
		let tmpTask = this._selectedTask || {};
		let tmpFields;
		if (this._shapeSegments.length)
		{
			tmpFields = this._shapeSegments.map((pSegment) => ''
				+ '<label class="ask-field">'
				+ '<span class="ask-field-label">' + this._esc(pSegment.Name) + (pSegment.Optional ? ' <em>(optional)</em>' : '') + '</span>'
				+ (pSegment.Guidance ? ('<span class="ask-field-hint">' + this._esc(pSegment.Guidance) + '</span>') : '')
				+ '<textarea class="ask-input" data-seg-key="' + this._esc(pSegment.Key) + '" rows="3"></textarea>'
				+ '</label>').join('');
		}
		else if (this._selectedHasShape)
		{
			// The task has a shape but every segment is Fixed (the admin's locked skeleton, e.g. a canned SQL
			// query): there is nothing for the person to fill in, so just offer the run.
			tmpFields = '<p class="ask-noinput">This task runs with no extra input. Click Ask to run it.</p>';
		}
		else
		{
			// No shape at all: a single free-text box, sent as { body } (the degenerate case the server understands).
			tmpFields = ''
				+ '<label class="ask-field"><span class="ask-field-label">Your question or request</span>'
				+ '<textarea class="ask-input" data-seg-key="body" rows="5"></textarea></label>';
		}
		this._paint(''
			+ '<div class="ask-form">'
			+ '<button class="ask-back" onclick="_Pict.views[\'Plansheet-Ask\'].backToCatalog()">&larr; All tasks</button>'
			+ '<h2 class="ask-form-title">' + this._esc(tmpTask.Name || 'Task') + '</h2>'
			+ (tmpTask.Description ? ('<div class="ask-form-desc">' + this._esc(tmpTask.Description) + '</div>') : '')
			+ tmpFields
			+ (tmpTask.AllowAttachments ? this._attachControlHTML() : '')
			+ '<div class="ask-actions"><button class="ask-submit" onclick="_Pict.views[\'Plansheet-Ask\'].submitAsk()">Ask</button>'
			+ '<span id="Ask-FormMsg" class="ask-formmsg"></span></div>'
			+ '</div>');
	}

	_collectInputValues()
	{
		let tmpValues = {};
		let tmpBody = this._body();
		if (!tmpBody) { return tmpValues; }
		let tmpInputs = tmpBody.querySelectorAll('.ask-input');
		for (let i = 0; i < tmpInputs.length; i++)
		{
			let tmpKey = tmpInputs[i].getAttribute('data-seg-key') || '';
			if (tmpKey) { tmpValues[tmpKey] = tmpInputs[i].value; }
		}
		return tmpValues;
	}

	async submitAsk()
	{
		if (!this._selectedTask) { return; }
		let tmpValues = this._collectInputValues();
		let tmpHasText = Object.keys(tmpValues).some((pKey) => String(tmpValues[pKey] || '').trim());
		let tmpAttachments = this._sendableAttachments();
		let tmpMsg = (typeof document !== 'undefined') ? document.getElementById('Ask-FormMsg') : null;
		if (this._hasPendingUploads()) { if (tmpMsg) { tmpMsg.textContent = 'A file is still uploading. One moment.'; } return; }
		// A no-input task (a fixed-shape task with no editable segments) submits with empty values -- the shape
		// carries everything. Demand text only when there is something to type (editable fields, or no shape) AND
		// nothing was attached: a file on its own is a valid ask for a conversational, attachment-taking task.
		let tmpNeedsText = this._shapeSegments.length > 0 || !this._selectedHasShape;
		if (tmpNeedsText && !tmpHasText && !tmpAttachments.length) { if (tmpMsg) { tmpMsg.textContent = 'Type something or attach a file first.'; } return; }
		if (tmpMsg) { tmpMsg.textContent = 'Submitting...'; }
		let tmpResult = null;
		try { tmpResult = await this._askAPI().submitRequest(Number(this._selectedTask.IDTask), tmpValues, '', tmpAttachments); }
		catch (pError)
		{
			if (tmpMsg) { tmpMsg.textContent = (pError && pError.message) || 'Could not submit that request.'; }
			return;
		}
		this._activeIDRequest = Number(tmpResult && tmpResult.IDRequest) || 0;
		if (!this._activeIDRequest) { if (tmpMsg) { tmpMsg.textContent = 'Could not open the request.'; } return; }
		this._pendingAttachments = [];
		// The running + answer state is its own route, so it deep-links and Back returns to the task form
		// rather than falling out of Ask entirely.
		this._goRequest(this._activeIDRequest);
	}

	// ---- running + answer ----------------------------------------------------------------------------------
	_paintRunning(pStage)
	{
		let tmpTask = this._selectedTask || {};
		this._paint(''
			+ '<div class="ask-run">'
			+ '<h2 class="ask-form-title">' + this._esc(tmpTask.Name || 'Request') + '</h2>'
			+ '<div class="ask-spinner"></div>'
			+ '<div class="ask-stage">' + this._esc(pStage || 'Working...') + '</div>'
			+ '</div>');
	}

	_startPoll()
	{
		this._pollToken++;
		let tmpToken = this._pollToken;
		let fTick = () =>
		{
			if (tmpToken !== this._pollToken) { return; }
			this._requestAPI().read(this._activeIDRequest).then((pRequest) =>
			{
				if (tmpToken !== this._pollToken) { return; }
				if (!pRequest) { setTimeout(fTick, _POLL_MS); return; }
				let tmpStatus = String(pRequest.Status || 'Pending');
				if (_REQUEST_TERMINAL[tmpStatus]) { this._paintAnswer(pRequest); return; }
				this._paintRunning(tmpStatus === 'Running' ? 'Working on it...' : 'Queued...');
				setTimeout(fTick, _POLL_MS);
			}).catch(() => { if (tmpToken === this._pollToken) { setTimeout(fTick, _POLL_MS); } });
		};
		setTimeout(fTick, _POLL_MS);
	}

	_paintAnswer(pRequest)
	{
		let tmpStatus = String(pRequest.Status || '');
		let tmpTitle = this._esc(pRequest.Name || 'Request');
		let tmpOutcome = String(pRequest.Outcome || '');
		// A document-kind outcome (Authoring Nodes V52) wrote its answer into the Docs Lake; OutcomeJSON carries
		// the pointer so the answer offers an "Open the article" jump to the doc reader.
		let tmpDoc = null;
		try { let tmpOJ = JSON.parse(pRequest.OutcomeJSON || '{}'); if (tmpOJ && tmpOJ.Kind === 'document' && tmpOJ.Path) { tmpDoc = tmpOJ; } }
		catch (pIgnore) { tmpDoc = null; }
		let tmpHTML = ''
			+ '<div class="ask-answer">'
			+ '<button class="ask-back" onclick="_Pict.views[\'Plansheet-Ask\'].backToCatalog()">&larr; All tasks</button>'
			+ '<h2 class="ask-form-title">' + tmpTitle + '</h2>'
			+ '<div class="ask-answer-status ask-pill ask-pill-' + tmpStatus.toLowerCase() + '">' + this._esc(tmpStatus) + '</div>';
		// Show what was asked, above the answer. The question (AssembledInput, or the raw inputs as a fallback)
		// is stored on the Request but was never surfaced here before.
		let tmpQuestion = this._questionText(pRequest);
		if (tmpQuestion)
		{
			tmpHTML += '<div class="ask-qblock"><div class="ask-qlabel">Your question</div><div class="ask-qtext">' + this._esc(tmpQuestion) + '</div></div>';
		}
		if (tmpStatus === 'Answered')
		{
			tmpHTML += '<div class="ask-alabel">Answer</div><div id="Ask-Outcome" class="ask-outcome pict-content"></div>';
		}
		else
		{
			tmpHTML += '<div class="ask-outcome ask-outcome-fail">' + (tmpOutcome ? this._esc(tmpOutcome) : 'The task did not complete.') + '</div>';
		}
		tmpHTML += '<div class="ask-actions">';
		if (tmpDoc)
		{
			tmpHTML += '<button class="ask-submit" onclick="_Pict.views[\'Plansheet-Ask\'].openArticle(\'' + encodeURIComponent(tmpDoc.Path) + '\')">Open the article</button>';
		}
		tmpHTML += '<button class="ask-submit" onclick="_Pict.views[\'Plansheet-Ask\'].backToCatalog()">Ask another</button>';
		tmpHTML += '<button class="ask-del-btn" onclick="_Pict.views[\'Plansheet-Ask\'].deleteRequest(' + (Number(pRequest.IDRequest) || 0) + ', true)">Delete</button>';
		tmpHTML += '</div></div>';
		this._paint(tmpHTML);
		if (tmpStatus === 'Answered')
		{
			let tmpContainer = (typeof document !== 'undefined') ? document.getElementById('Ask-Outcome') : null;
			if (tmpContainer)
			{
				// Render the answer as markdown when the content provider is available; fall back to escaped text.
				if (this.pict.providers.Content && typeof libMarkdownRender.renderInto === 'function')
				{
					libMarkdownRender.renderInto(this.pict.providers.Content, tmpContainer, tmpOutcome, '<em>No answer text.</em>', true);
				}
				else { tmpContainer.textContent = tmpOutcome; }
			}
		}
	}

	// The human-readable question that was filed. AssembledInput is the composed prompt the node received; fall
	// back to the raw submitted field values when a task stored nothing assembled (e.g. a bare-body ask).
	_questionText(pRequest)
	{
		let tmpAssembled = String((pRequest && pRequest.AssembledInput) || '').trim();
		if (tmpAssembled) { return tmpAssembled; }
		let tmpValues = null;
		try { tmpValues = JSON.parse((pRequest && pRequest.InputValuesJSON) || 'null'); } catch (pError) { tmpValues = null; }
		if (tmpValues && typeof tmpValues === 'object')
		{
			return Object.keys(tmpValues).map((pKey) => String(tmpValues[pKey] || '').trim()).filter((pValue) => pValue).join('\n\n');
		}
		return '';
	}

	// Delete (soft, owner-scoped on the server) one of my requests, behind a confirm. From the detail we navigate
	// back to the catalog (the request is gone); from the catalog we drop it from the in-memory list and repaint.
	async deleteRequest(pIDRequest, pFromDetail)
	{
		let tmpID = Number(pIDRequest) || 0;
		if (!tmpID) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpOk = true;
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			try { tmpOk = await tmpModal.confirm('Delete this request? This cannot be undone.', { dangerous: true }); }
			catch (pError) { tmpOk = false; }
		}
		if (!tmpOk) { return; }
		try { await this._requestAPI().remove(tmpID); }
		catch (pError)
		{
			if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast((pError && pError.message) || 'Could not delete that request.', { type: 'error' }); }
			return;
		}
		this._myRequests = this._myRequests.filter((pRequest) => Number(pRequest.IDRequest) !== tmpID);
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('Request deleted.', { type: 'success' }); }
		if (pFromDetail) { this._goCatalog(); }
		else { this._paintCatalog(); }
	}

	// ---- attachments (the Media buffer) --------------------------------------------------------------------
	// A file-attach control: a hidden file input plus a button and the chips for what is staged. Shared by the
	// task form (first turn) and the chat composer (follow-ups); both reuse the #Ask-File / #Ask-Chips ids since
	// only one is on screen at a time.
	_attachControlHTML()
	{
		return ''
			+ '<div class="ask-attach">'
			+ '<input type="file" id="Ask-File" class="ask-file" multiple onchange="_Pict.views[\'Plansheet-Ask\'].attachFiles(this)" />'
			+ '<button type="button" class="ask-attach-btn" onclick="document.getElementById(\'Ask-File\').click()">Attach a file</button>'
			+ '<span class="ask-chips" id="Ask-Chips">' + this._chipsHTML() + '</span>'
			+ '</div>';
	}

	_chipsHTML()
	{
		return this._pendingAttachments.map((pAtt, pIdx) => ''
			+ '<span class="ask-chip' + (pAtt.Pending ? ' ask-chip-pending' : '') + '">'
			+ '<span class="ask-chip-name">' + this._esc(pAtt.Name || 'file') + '</span>'
			+ (pAtt.Pending ? '<span class="ask-chip-spin"></span>'
				: '<button type="button" class="ask-chip-x" title="Remove" onclick="_Pict.views[\'Plansheet-Ask\'].removeAttachment(' + pIdx + ')">&times;</button>')
			+ '</span>').join('');
	}

	_repaintChips()
	{
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Ask-Chips') : null;
		if (tmpEl) { tmpEl.innerHTML = this._chipsHTML(); }
	}

	// Upload each chosen file to the Media buffer as TEAM media (never Private -- a node persona must be able to
	// read it) and stage a reference. A placeholder chip shows while the upload runs.
	async attachFiles(pInputEl)
	{
		if (!pInputEl || !pInputEl.files || !pInputEl.files.length) { return; }
		let tmpFiles = Array.prototype.slice.call(pInputEl.files);
		pInputEl.value = '';	// let the same file be chosen again later
		for (let i = 0; i < tmpFiles.length; i++)
		{
			let tmpFile = tmpFiles[i];
			let tmpPlaceholder = { Name: tmpFile.name, Pending: true };
			this._pendingAttachments.push(tmpPlaceholder);
			this._repaintChips();
			try
			{
				let tmpResult = await this._mediaAPI().upload('RequestUpload', 0, tmpFile, {});
				let tmpIDMedia = Number(tmpResult && (tmpResult.IDMedia || tmpResult.ID)) || 0;
				let tmpIdx = this._pendingAttachments.indexOf(tmpPlaceholder);
				if (!tmpIDMedia)
				{
					if (tmpIdx >= 0) { this._pendingAttachments.splice(tmpIdx, 1); }
					this._toast('Could not upload ' + tmpFile.name + '.', 'error');
				}
				else
				{
					let tmpRef = { Source: 'media', Ref: tmpIDMedia, Name: String(tmpFile.name || ''), ContentType: String(tmpFile.type || ''), Size: Number(tmpFile.size) || 0 };
					if (tmpIdx >= 0) { this._pendingAttachments.splice(tmpIdx, 1, tmpRef); } else { this._pendingAttachments.push(tmpRef); }
				}
			}
			catch (pError)
			{
				let tmpIdx = this._pendingAttachments.indexOf(tmpPlaceholder);
				if (tmpIdx >= 0) { this._pendingAttachments.splice(tmpIdx, 1); }
				this._toast((pError && pError.message) || ('Could not upload ' + tmpFile.name + '.'), 'error');
			}
			this._repaintChips();
		}
	}

	removeAttachment(pIdx)
	{
		let tmpIdx = Number(pIdx);
		if (tmpIdx >= 0 && tmpIdx < this._pendingAttachments.length) { this._pendingAttachments.splice(tmpIdx, 1); this._repaintChips(); }
	}

	// Only fully-uploaded references (placeholders still uploading are skipped) are sent with a turn.
	_sendableAttachments()
	{
		return this._pendingAttachments.filter((pAtt) => pAtt && !pAtt.Pending && pAtt.Ref);
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && tmpModal.toast) { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}

	// ---- the chat (conversational requests) ----------------------------------------------------------------
	async _loadThread(pIDRequest)
	{
		let tmpRows = [];
		try { tmpRows = await this._messageAPI().resolve({ Filter: 'FBV~IDRequest~EQ~' + (Number(pIDRequest) || 0), PageSize: 500 }); }
		catch (pError) { tmpRows = []; }
		if (!Array.isArray(tmpRows)) { return []; }
		return tmpRows.sort((pA, pB) => (Number(pA.Sequence) || 0) - (Number(pB.Sequence) || 0));
	}

	_threadBusy(pMessages)
	{
		return pMessages.some((pMsg) => String(pMsg.Role) === 'assistant' && (String(pMsg.Status) === 'Pending' || String(pMsg.Status) === 'Running'));
	}

	// A cheap signature so the poll repaints only when the thread actually changed (no flicker on an idle tick).
	_threadSig(pMessages)
	{
		return pMessages.map((pMsg) => (Number(pMsg.IDRequestMessage) || 0) + ':' + String(pMsg.Status || '') + ':' + String(pMsg.Content || '').length).join('|');
	}

	_paintChat(pRequest, pMessages)
	{
		let tmpBusy = this._threadBusy(pMessages);
		let tmpParts = [];
		tmpParts.push('<div class="ask-chat">');
		tmpParts.push('<button class="ask-back" onclick="_Pict.views[\'Plansheet-Ask\'].backToCatalog()">&larr; All tasks</button>');
		tmpParts.push('<div class="ask-chat-head"><h2 class="ask-form-title">' + this._esc(pRequest.Name || 'Request') + '</h2>'
			+ '<button class="ask-hist-del ask-chat-del" title="Delete this conversation" aria-label="Delete this conversation" onclick="_Pict.views[\'Plansheet-Ask\'].deleteRequest(' + (Number(pRequest.IDRequest) || 0) + ', true)">' + _TRASH_SVG + '</button></div>');
		tmpParts.push('<div class="ask-thread" id="Ask-Thread">');
		pMessages.forEach((pMsg) => { tmpParts.push(this._messageHTML(pMsg)); });
		tmpParts.push('</div>');
		tmpParts.push('<div class="ask-composer">');
		tmpParts.push('<textarea class="ask-input ask-composer-text" id="Ask-Composer" rows="2" placeholder="' + (tmpBusy ? 'Waiting for the assistant...' : 'Reply, or ask for a change...') + '"' + (tmpBusy ? ' disabled' : '') + '></textarea>');
		tmpParts.push('<div class="ask-composer-row">' + (this._chatAllowAttachments ? this._attachControlHTML() : '<span></span>')
			+ '<button class="ask-submit ask-send" onclick="_Pict.views[\'Plansheet-Ask\'].sendMessage()"' + (tmpBusy ? ' disabled' : '') + '>Send</button></div>');
		tmpParts.push('</div></div>');
		this._paint(tmpParts.join(''));
		this._renderAssistantMarkdown(pMessages);
		this._scrollThreadToEnd();
		this._lastThreadSig = this._threadSig(pMessages);
	}

	_messageHTML(pMsg)
	{
		let tmpIsAssistant = (String(pMsg.Role || 'user') === 'assistant');
		let tmpStatus = String(pMsg.Status || '');
		let tmpContent = String(pMsg.Content || '');
		let tmpBody;
		if (!tmpIsAssistant)
		{
			tmpBody = this._esc(tmpContent);
		}
		else if (tmpContent.trim())
		{
			// Rendered as markdown after paint into this container (keyed by message id).
			tmpBody = '<div class="pict-content ask-md" data-md-id="' + (Number(pMsg.IDRequestMessage) || 0) + '"></div>';
		}
		else if (tmpStatus === 'Pending' || tmpStatus === 'Running')
		{
			tmpBody = '<span class="ask-typing"><span></span><span></span><span></span></span>';
		}
		else
		{
			tmpBody = '<span class="ask-stage">' + this._esc(tmpStatus || 'No reply') + '</span>';
		}
		return ''
			+ '<div class="ask-msg ask-msg-' + (tmpIsAssistant ? 'assistant' : 'user') + '">'
			+ '<div class="ask-msg-role">' + (tmpIsAssistant ? 'Assistant' : 'You') + '</div>'
			+ '<div class="ask-msg-body">' + tmpBody + '</div>'
			+ this._messageAttachmentsHTML(pMsg)
			+ (tmpIsAssistant ? this._messageMetaHTML(pMsg) : '')
			+ '</div>';
	}

	_messageAttachmentsHTML(pMsg)
	{
		let tmpList = [];
		try { tmpList = JSON.parse(pMsg.AttachmentsJSON || '[]'); } catch (pError) { tmpList = []; }
		if (!Array.isArray(tmpList) || !tmpList.length) { return ''; }
		let tmpChips = tmpList.map((pAtt) => '<span class="ask-chip ask-chip-static">' + this._esc((pAtt && (pAtt.Name || pAtt.OriginalFileName)) || 'file') + '</span>').join('');
		return '<div class="ask-msg-atts">' + tmpChips + '</div>';
	}

	// The runner's opaque MetadataJSON may carry a produced artifact; surface a Headlight form link when present.
	_messageMetaHTML(pMsg)
	{
		let tmpMeta = null;
		try { tmpMeta = JSON.parse(pMsg.MetadataJSON || 'null'); } catch (pError) { tmpMeta = null; }
		if (!tmpMeta || typeof tmpMeta !== 'object') { return ''; }
		let tmpForm = tmpMeta.HeadlightForm || tmpMeta.Form || null;
		if (tmpForm && tmpForm.url)
		{
			return '<div class="ask-msg-meta"><a class="ask-meta-link" href="' + this._esc(tmpForm.url) + '" target="_blank" rel="noopener noreferrer">Open in Headlight &rarr;</a></div>';
		}
		return '';
	}

	_renderAssistantMarkdown(pMessages)
	{
		if (typeof document === 'undefined') { return; }
		pMessages.forEach((pMsg) =>
		{
			if (String(pMsg.Role) !== 'assistant') { return; }
			let tmpText = String(pMsg.Content || '');
			if (!tmpText.trim()) { return; }
			let tmpContainer = document.querySelector('.ask-md[data-md-id="' + (Number(pMsg.IDRequestMessage) || 0) + '"]');
			if (!tmpContainer) { return; }
			if (this.pict.providers.Content && typeof libMarkdownRender.renderInto === 'function')
			{
				libMarkdownRender.renderInto(this.pict.providers.Content, tmpContainer, tmpText, '<em>No reply.</em>', true);
			}
			else { tmpContainer.textContent = tmpText; }
		});
	}

	_scrollThreadToEnd()
	{
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Ask-Thread') : null;
		if (tmpEl) { tmpEl.scrollTop = tmpEl.scrollHeight; }
	}

	// Send a follow-up turn: post it, then reload the thread (now with the new user turn + a Pending assistant)
	// and poll for the reply. Owner-scoped on the server; a turn is refused while one is still running.
	async sendMessage()
	{
		if (!this._activeIDRequest) { return; }
		if (this._hasPendingUploads()) { this._toast('A file is still uploading. One moment.', 'error'); return; }
		let tmpToken = this._pollToken;
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Ask-Composer') : null;
		let tmpText = tmpEl ? String(tmpEl.value || '').trim() : '';
		let tmpAttachments = this._sendableAttachments();
		if (!tmpText && !tmpAttachments.length) { return; }
		if (tmpEl) { tmpEl.disabled = true; }
		try { await this._askAPI().postMessage(this._activeIDRequest, tmpText, tmpAttachments); }
		catch (pError)
		{
			if (tmpEl) { tmpEl.disabled = false; }
			this._toast((pError && pError.message) || 'Could not send that message.', 'error');
			return;
		}
		if (tmpToken !== this._pollToken) { return; }	// navigated away while sending
		this._pendingAttachments = [];
		let tmpTitle = (this._selectedTask && this._selectedTask.Name) || 'Request';
		let tmpMessages = await this._loadThread(this._activeIDRequest);
		if (tmpToken !== this._pollToken) { return; }
		this._paintChat({ IDRequest: this._activeIDRequest, Name: tmpTitle }, tmpMessages);
		if (this._threadBusy(tmpMessages)) { this._startThreadPoll(); }
	}

	// Poll a conversational thread while a turn is in flight; repaint only when the thread changes, and stop once
	// the assistant turn is terminal. Guarded by the shared _pollToken so a navigation cancels it.
	_startThreadPoll()
	{
		this._pollToken++;
		let tmpToken = this._pollToken;
		let tmpIDRequest = this._activeIDRequest;
		let tmpTitle = (this._selectedTask && this._selectedTask.Name) || 'Request';
		let fTick = () =>
		{
			if (tmpToken !== this._pollToken) { return; }
			this._loadThread(tmpIDRequest).then((pMessages) =>
			{
				if (tmpToken !== this._pollToken) { return; }
				if (this._threadSig(pMessages) !== this._lastThreadSig) { this._paintChat({ IDRequest: tmpIDRequest, Name: tmpTitle }, pMessages); }
				if (this._threadBusy(pMessages)) { setTimeout(fTick, _POLL_MS); }
			}).catch(() => { if (tmpToken === this._pollToken) { setTimeout(fTick, _POLL_MS); } });
		};
		setTimeout(fTick, _POLL_MS);
	}

	// Jump to the doc reader for a document-kind outcome. The path is passed URI-encoded (no quotes/spaces in the
	// inline handler); the docs route decodes its wildcard, so it is re-encoded for the navigate.
	openArticle(pEncodedPath)
	{
		let tmpPath = '';
		try { tmpPath = decodeURIComponent(String(pEncodedPath || '')); } catch (pError) { tmpPath = String(pEncodedPath || ''); }
		if (!tmpPath) { return; }
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter && typeof tmpRouter.navigate === 'function') { tmpRouter.navigate('/docs/' + encodeURIComponent(tmpPath)); }
	}

	// A history row click is a navigation to /ask/request/:id; the route handler calls showRequest to load + paint.
	openRequest(pIDRequest) { this._goRequest(pIDRequest); }

	async showRequest(pIDRequest)
	{
		this._ensureShell();
		this._pollToken++;
		let tmpToken = this._pollToken;
		this._pendingAttachments = [];
		this._lastThreadSig = '';
		this._chatAllowAttachments = false;
		this._paint('<div class="ask-loading">Loading...</div>');
		let tmpRequest = null;
		try { tmpRequest = await this._requestAPI().read(Number(pIDRequest)); }
		catch (pError) { tmpRequest = null; }
		if (tmpToken !== this._pollToken) { return; }	// navigated away during the read
		if (!tmpRequest) { return this.showCatalog(); }
		this._selectedTask = { Name: tmpRequest.Name };
		this._activeIDRequest = Number(tmpRequest.IDRequest) || 0;
		// A conversational request has a message thread: render the chat and poll any in-flight turn. A single-shot
		// request has no messages, so it reopens into the running view or its one answer, exactly as before.
		let tmpMessages = await this._loadThread(this._activeIDRequest);
		if (tmpToken !== this._pollToken) { return; }
		if (tmpMessages.length)
		{
			// Learn whether the task still permits attachments, so the composer's attach control matches the form.
			if (Number(tmpRequest.IDTask) > 0)
			{
				let tmpTask = null;
				try { tmpTask = await this._taskAPI().read(Number(tmpRequest.IDTask)); } catch (pError) { tmpTask = null; }
				if (tmpToken !== this._pollToken) { return; }
				this._chatAllowAttachments = !!(tmpTask && tmpTask.AllowAttachments);
			}
			this._paintChat(tmpRequest, tmpMessages);
			if (this._threadBusy(tmpMessages)) { this._startThreadPoll(); }
			return;
		}
		let tmpStatus = String(tmpRequest.Status || '');
		// A request still working reopens into the live view; a finished one shows its answer.
		if (_REQUEST_TERMINAL[tmpStatus]) { this._paintAnswer(tmpRequest); }
		else { this._paintRunning('Working on it...'); this._startPoll(); }
	}

	backToCatalog() { this._goCatalog(); }

	// Navigation helpers: drive every state change through the router (app._navigate) so Back / forward and
	// reload behave and each state deep-links. Fall back to an in-place render when the app/router is not
	// reachable (e.g. a unit test that instantiates the view without the application).
	_goCatalog() { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showAsk === 'function') { tmpApp.showAsk(); } else { this.showCatalog(); } }
	_goTask(pIDTask) { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showAskTask === 'function') { tmpApp.showAskTask(Number(pIDTask) || 0); } else { this.showTask(pIDTask); } }
	_goRequest(pIDRequest) { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showAskRequest === 'function') { tmpApp.showAskRequest(Number(pIDRequest) || 0); } else { this.showRequest(pIDRequest); } }
}

const PictViewAskConfiguration =
{
	ViewIdentifier: 'Plansheet-Ask',
	DefaultRenderable: 'Plansheet-Ask',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ask-wrap { max-width: 900px; margin: 0 auto; padding: 24px 16px 64px; }
		.ask-head h1 { margin: 0 0 4px; font-size: 1.6em; color: var(--theme-color-text-primary, #1d2230); }
		.ask-sub { margin: 0 0 20px; color: var(--theme-color-text-secondary, #5b6376); }
		.ask-body { display: block; }
		.ask-loading, .ask-empty { color: var(--theme-color-text-secondary, #5b6376); padding: 24px 0; }
		.ask-group { margin-bottom: 24px; }
		.ask-group-head { font-size: 0.8em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 10px; }
		.ask-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
		.ask-card { text-align: left; cursor: pointer; padding: 14px 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); transition: border-color 0.12s, box-shadow 0.12s; }
		.ask-card:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); box-shadow: 0 2px 10px rgba(0,0,0,0.06); }
		.ask-card-name { font-weight: 600; margin-bottom: 4px; }
		.ask-card-sum { font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.4; }
		.ask-back { background: none; border: none; color: var(--theme-color-brand-primary, #3b6fd4); cursor: pointer; padding: 0; font-size: 0.9em; margin-bottom: 12px; }
		.ask-form-title { margin: 0 0 8px; font-size: 1.3em; color: var(--theme-color-text-primary, #1d2230); }
		.ask-form-desc { color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 16px; line-height: 1.5; }
		.ask-field { display: block; margin-bottom: 16px; }
		.ask-field-label { display: block; font-weight: 600; margin-bottom: 4px; color: var(--theme-color-text-primary, #1d2230); }
		.ask-field-label em { font-weight: 400; color: var(--theme-color-text-secondary, #5b6376); }
		.ask-field-hint { display: block; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 6px; }
		.ask-noinput { color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 16px; line-height: 1.5; }
		.ask-input { width: 100%; padding: 10px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); font: inherit; resize: vertical; box-sizing: border-box; }
		.ask-actions { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
		.ask-submit { padding: 9px 20px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.95em; }
		.ask-del-btn { padding: 9px 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: none; color: var(--theme-color-status-error, #c0353a); cursor: pointer; font-size: 0.95em; }
		.ask-del-btn:hover { border-color: var(--theme-color-status-error, #c0353a); background: var(--theme-color-status-error-background, rgba(198,40,40,0.08)); }
		.ask-formmsg { font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); }
		.ask-run { text-align: center; padding: 40px 0; }
		.ask-spinner { width: 34px; height: 34px; margin: 16px auto; border: 3px solid var(--theme-color-border-default, #d8dde6); border-top-color: var(--theme-color-brand-primary, #3b6fd4); border-radius: 50%; animation: ask-spin 0.8s linear infinite; }
		@keyframes ask-spin { to { transform: rotate(360deg); } }
		.ask-stage { color: var(--theme-color-text-secondary, #5b6376); }
		.ask-answer-status { display: inline-block; margin-bottom: 14px; }
		.ask-qblock { margin: 0 0 18px; padding: 12px 14px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-left: 3px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-background-secondary, #f3f5f9); }
		.ask-qlabel, .ask-alabel { font-size: 0.72em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 5px; }
		.ask-alabel { margin-top: 2px; }
		.ask-qtext { color: var(--theme-color-text-primary, #1d2230); line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
		.ask-outcome { border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px; padding: 16px; background: var(--theme-color-background-secondary, #f3f5f9); line-height: 1.55; color: var(--theme-color-text-primary, #1d2230); white-space: pre-wrap; }
		.ask-outcome-fail { color: var(--theme-color-status-error, #c0353a); white-space: pre-wrap; }
		.ask-hist { margin-top: 32px; border-top: 1px solid var(--theme-color-border-default, #d8dde6); padding-top: 18px; }
		.ask-hist-row { display: flex; align-items: center; gap: 2px; border-radius: 8px; }
		.ask-hist-row:hover { background: var(--theme-color-background-hover, #f3f5f9); }
		.ask-hist-open { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; justify-content: space-between; gap: 10px; text-align: left; cursor: pointer; padding: 9px 12px; border: none; border-radius: 8px; background: none; color: var(--theme-color-text-primary, #1d2230); font: inherit; }
		.ask-hist-del { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; margin-right: 6px; padding: 0; border: none; border-radius: 7px; background: none; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; opacity: 0.5; transition: opacity 0.12s, color 0.12s, background 0.12s; }
		.ask-hist-row:hover .ask-hist-del { opacity: 1; }
		.ask-hist-del:hover { color: var(--theme-color-status-error, #c0353a); background: var(--theme-color-status-error-background, rgba(198,40,40,0.12)); }
		.ask-hist-del svg { width: 15px; height: 15px; }
		.ask-hist-name { font-size: 0.92em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ask-pill { display: inline-block; font-size: 0.62em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 9px; border-radius: 11px; }
		.ask-pill-pending { background: #9e9e9e; color: #fff; }
		.ask-pill-running { background: #F6C453; color: #3a2f00; }
		.ask-pill-answered { background: #2E7D32; color: #fff; }
		.ask-pill-failed, .ask-pill-cancelled { background: #C62828; color: #fff; }
		.ask-attach { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
		.ask-file { display: none; }
		.ask-attach-btn { padding: 7px 12px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: none; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; font-size: 0.88em; }
		.ask-attach-btn:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-brand-primary, #3b6fd4); }
		.ask-chips { display: inline-flex; gap: 6px; flex-wrap: wrap; }
		.ask-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 10px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); font-size: 0.82em; color: var(--theme-color-text-primary, #1d2230); }
		.ask-chip-static { padding: 4px 10px; }
		.ask-chip-name { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ask-chip-x { border: none; background: none; cursor: pointer; color: var(--theme-color-text-secondary, #5b6376); font-size: 1.1em; line-height: 1; padding: 0 2px; }
		.ask-chip-x:hover { color: var(--theme-color-status-error, #c0353a); }
		.ask-chip-pending { opacity: 0.7; }
		.ask-chip-spin { width: 11px; height: 11px; border: 2px solid var(--theme-color-border-default, #d8dde6); border-top-color: var(--theme-color-brand-primary, #3b6fd4); border-radius: 50%; animation: ask-spin 0.8s linear infinite; display: inline-block; }
		.ask-chat { display: flex; flex-direction: column; }
		.ask-chat-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
		.ask-thread { display: flex; flex-direction: column; gap: 14px; max-height: 60vh; overflow-y: auto; padding: 6px 2px 12px; }
		.ask-msg { max-width: 85%; }
		.ask-msg-user { align-self: flex-end; }
		.ask-msg-assistant { align-self: flex-start; }
		.ask-msg-role { font-size: 0.68em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 4px; }
		.ask-msg-body { display: inline-block; text-align: left; padding: 10px 13px; border-radius: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
		.ask-msg-user .ask-msg-body { background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; border-bottom-right-radius: 4px; }
		.ask-msg-assistant .ask-msg-body { background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); border: 1px solid var(--theme-color-border-default, #d8dde6); border-bottom-left-radius: 4px; }
		.ask-msg-body .ask-md { white-space: normal; }
		.ask-msg-atts { margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap; }
		.ask-msg-user .ask-msg-atts { justify-content: flex-end; }
		.ask-msg-meta { margin-top: 6px; }
		.ask-meta-link { font-size: 0.88em; color: var(--theme-color-brand-primary, #3b6fd4); text-decoration: none; }
		.ask-meta-link:hover { text-decoration: underline; }
		.ask-typing { display: inline-flex; gap: 4px; align-items: center; }
		.ask-typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--theme-color-text-secondary, #5b6376); opacity: 0.5; animation: ask-bounce 1.2s infinite ease-in-out; }
		.ask-typing span:nth-child(2) { animation-delay: 0.15s; }
		.ask-typing span:nth-child(3) { animation-delay: 0.3s; }
		@keyframes ask-bounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.4; } 30% { transform: translateY(-4px); opacity: 1; } }
		.ask-composer { margin-top: 16px; border-top: 1px solid var(--theme-color-border-default, #d8dde6); padding-top: 14px; }
		.ask-composer-text { margin-bottom: 8px; }
		.ask-composer-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
		.ask-send { flex: 0 0 auto; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Ask',
			Template: /*html*/`
<div class="ask-wrap">
	<div class="ask-head">
		<h1>Ask</h1>
		<p class="ask-sub">Pick a task, describe what you need, and a runner brings back the answer.</p>
	</div>
	<div id="Ask-Body" class="ask-body"></div>
</div>`
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-Ask', TemplateHash: 'Plansheet-Ask', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

module.exports = PictViewAsk;
module.exports.default_configuration = PictViewAskConfiguration;
